import type { ReactNode } from 'react';

import type { DrawerHeaderOptions } from './types/DrawerHeaderOptions';
import { LayerActionType } from './types/LayerActionType';
import { LayerStatus } from './types/LayerStatus';

export interface Layer {
    header: DrawerHeaderOptions;
    content: ReactNode;
    width?: `${number}${'vw' | 'px'}`;
    offsetParent?: boolean;
    /** Called once when closure is accepted, before the exit animation finishes. */
    onPop?: () => void;
}

export interface DrawerConfirmationOptions {
    title: string;
    description: string;
    confirmText: string;
    destructive?: boolean;
}

export type StoredLayer = Layer & { id: number } & (
        | { status: LayerStatus.OPEN; confirmation?: never }
        | { status: LayerStatus.CONFIRMING; confirmation: DrawerConfirmationOptions }
        | { status: LayerStatus.CLOSING; confirmation?: never }
    );

export type LayerAction =
    | { type: LayerActionType.PUSH; layer: StoredLayer }
    | { type: LayerActionType.CLOSE; id: number }
    | { type: LayerActionType.REMOVE; id: number }
    | { type: LayerActionType.CONFIRM; id: number; confirmation: DrawerConfirmationOptions }
    | { type: LayerActionType.CLEAR_CONFIRMATION; id: number };

export interface LayerStoreState {
    layers: StoredLayer[];
}

export const layerStoreReducer = (state: LayerStoreState, action: LayerAction): LayerStoreState => {
    switch (action.type) {
        case LayerActionType.PUSH:
            return { layers: [...state.layers, action.layer] };
        case LayerActionType.CLOSE:
            return {
                layers: state.layers.map((layer) =>
                    layer.id === action.id ? { ...layer, status: LayerStatus.CLOSING, confirmation: undefined } : layer
                ),
            };
        case LayerActionType.REMOVE:
            return {
                layers: state.layers.filter((layer) => layer.id !== action.id || layer.status !== LayerStatus.CLOSING),
            };
        case LayerActionType.CONFIRM:
            return {
                layers: state.layers.map((layer) =>
                    layer.id === action.id && layer.status === LayerStatus.OPEN
                        ? { ...layer, status: LayerStatus.CONFIRMING, confirmation: action.confirmation }
                        : layer
                ),
            };
        case LayerActionType.CLEAR_CONFIRMATION:
            return {
                layers: state.layers.map((layer) =>
                    layer.id === action.id && layer.status === LayerStatus.CONFIRMING
                        ? { ...layer, status: LayerStatus.OPEN, confirmation: undefined }
                        : layer
                ),
            };
    }
};
